<template>
  <div class="crm-container">
    <el-card v-loading="configLoading">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('crm.list.title') }}</span>
        <div class="crm-status-box">
          <el-tag v-if="crmConfig.crmOpen" type="success" size="small">
            {{ $t('crm.list.crmEnabled') }}
          </el-tag>
          <el-tag v-else type="info" size="small">
            {{ $t('crm.list.crmDisabled') }}
          </el-tag>
          <el-button
            v-if="crmConfig.crmOpen && crmConfig.crmUrl"
            size="mini"
            icon="el-icon-connection"
            :loading="testing"
            @click="handleTestConnection"
          >
            {{ $t('crm.list.testConnection') }}
          </el-button>
          <span v-if="crmConfig.crmOpen && crmConfig.crmUrl" class="crm-url-text">
            {{ $t('crm.list.crmUrlLabel') }}：
            <a :href="crmConfig.crmUrl" target="_blank" class="crm-url-link">{{ crmConfig.crmUrl }}</a>
          </span>
        </div>
      </div>

      <div v-if="crmConfig.crmOpen">
        <div class="search-wrapper">
          <el-row :gutter="20">
            <el-col :span="5">
              <el-input v-model="search.ownerName" :placeholder="$t('crm.search.ownerName')" clearable />
            </el-col>
            <el-col :span="5">
              <el-input v-model="search.mobile" :placeholder="$t('crm.search.mobile')" clearable />
            </el-col>
            <el-col :span="5">
              <el-select v-model="search.result" :placeholder="$t('crm.search.result')" clearable>
                <el-option :label="$t('crm.list.success')" value="SUCCESS" />
                <el-option :label="$t('crm.list.fail')" value="FAIL" />
              </el-select>
            </el-col>
            <el-col :span="4">
              <el-select v-model="search.userType" :placeholder="$t('crm.search.userType')" clearable>
                <el-option :label="$t('crm.table.verified')" value="VERIFIED" />
                <el-option :label="$t('crm.table.unverified')" value="UNVERIFIED" />
              </el-select>
            </el-col>
            <el-col :span="5">
              <el-button type="primary" @click="handleSearch">
                {{ $t('common.search') }}
              </el-button>
              <el-button @click="handleReset">
                {{ $t('common.reset') }}
              </el-button>
            </el-col>
          </el-row>
        </div>

        <div class="list-wrapper">
          <div class="table-toolbar">
            <el-button type="primary" size="small" icon="el-icon-upload" @click="handleUpload">
              {{ $t('crm.list.uploadBtn') }}
            </el-button>
          </div>

          <el-table v-loading="loading" :data="tableData" border style="width:100%">
            <el-table-column prop="ownerName" :label="$t('crm.table.ownerName')" align="center" />
            <el-table-column prop="mobile" :label="$t('crm.table.mobile')" align="center" width="140" />
            <el-table-column :label="$t('crm.table.userType')" align="center" width="120">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.ownerName === scope.row.mobile && !scope.row.communityName" type="warning" size="mini">
                  {{ $t('crm.table.unverified') }}
                </el-tag>
                <el-tag v-else type="success" size="mini">
                  {{ $t('crm.table.verified') }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('crm.table.communityName')" align="center" show-overflow-tooltip />
            <el-table-column :label="$t('crm.table.crmUserId')" align="center" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="!scope.row.crmUserId" class="crm-skip">{{ $t('crm.table.skipQuery') }}</span>
                <span v-else>{{ scope.row.crmUserId }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('crm.table.result')" align="center" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.result === 'SUCCESS' ? 'success' : 'danger'">
                  {{ scope.row.result === 'SUCCESS' ? $t('crm.list.success') : $t('crm.list.fail') }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="resultMsg" :label="$t('crm.table.resultMsg')" align="center" show-overflow-tooltip />
            <el-table-column prop="createTime" :label="$t('crm.table.createTime')" align="center" width="160" />
            <el-table-column prop="updateTime" :label="$t('crm.table.updateTime')" align="center" width="160" />
            <el-table-column :label="$t('crm.table.updateAgo')" align="center" width="140">
              <template slot-scope="scope">
                <span :class="getTimeClass(scope.row.updateTime)">
                  {{ getRelativeTime(scope.row.updateTime) }}
                </span>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            :current-page="page.current"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="page.size"
            :total="page.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>

      <div v-else class="crm-disabled-tip">
        <el-alert :title="$t('crm.list.disabledTip')" type="info" :closable="false" show-icon />
      </div>
    </el-card>

    <upload-owner-to-crm ref="uploadOwner" @success="getList" />
  </div>
</template>

<script>
import { getCrmConfig, listCrmUploadLogs, testCrmConnection } from '@/api/crm/crmUploadApi'
import UploadOwnerToCrm from '@/components/crm/uploadOwnerToCrm'

export default {
  name: 'CrmUserManageList',
  components: {
    UploadOwnerToCrm
  },
  data() {
    return {
      configLoading: false,
      testing: false,
      nowTimer: null,
      now: Date.now(),
      crmConfig: {
        crmOpen: false,
        crmUrl: ''
      },
      loading: false,
      search: {
        ownerName: '',
        mobile: '',
        result: '',
        userType: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getConfig()
    this.nowTimer = setInterval(() => {
      this.now = Date.now()
    }, 60 * 1000)
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
      this.nowTimer = null
    }
  },
  methods: {
    async getConfig() {
      try {
        this.configLoading = true
        const res = await getCrmConfig()
        this.crmConfig = res.data || { crmOpen: false, crmUrl: '' }
        if (this.crmConfig.crmOpen) {
          this.getList()
        }
      } catch (error) {
        this.$message.error(this.$t('crm.list.configError'))
      } finally {
        this.configLoading = false
      }
    },
    async getList() {
      try {
        this.loading = true
        const params = {
          page: this.page.current,
          row: this.page.size,
          ownerName: this.search.ownerName,
          mobile: this.search.mobile,
          result: this.search.result
        }
        const res = await listCrmUploadLogs(params)
        let list = res.data || []
        if (this.search.userType === 'VERIFIED') {
          list = list.filter(item => !(item.ownerName === item.mobile && !item.communityName))
        } else if (this.search.userType === 'UNVERIFIED') {
          list = list.filter(item => item.ownerName === item.mobile && !item.communityName)
        }
        this.tableData = list
        this.page.total = res.total || 0
      } catch (error) {
        this.$message.error(this.$t('crm.list.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.search = {
        ownerName: '',
        mobile: '',
        result: '',
        userType: ''
      }
      this.page.current = 1
      this.getList()
    },
    handleUpload() {
      this.$refs.uploadOwner.open()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    async handleTestConnection() {
      if (!this.crmConfig.crmUrl) {
        this.$message.warning(this.$t('crm.list.crmUrlEmpty'))
        return
      }
      this.testing = true
      try {
        await testCrmConnection(this.crmConfig.crmUrl)
        this.$message.success(this.$t('crm.list.testSuccess'))
      } catch (err) {
        this.$message.error(this.$t('crm.list.testFail') + '：' + (err.message || '未知错误'))
      } finally {
        this.testing = false
      }
    },
    getRelativeTime(timeStr) {
      if (!timeStr) return '-'
      const target = new Date(timeStr.replace(/-/g, '/')).getTime()
      if (isNaN(target)) return '-'
      const diff = this.now - target
      if (diff < 0) return this.$t('crm.table.justNow')
      const sec = Math.floor(diff / 1000)
      if (sec < 60) return this.$t('crm.table.minutesAgo', { n: sec })
      const min = Math.floor(sec / 60)
      if (min < 60) return this.$t('crm.table.minutesAgo', { n: min })
      const hour = Math.floor(min / 60)
      if (hour < 24) return this.$t('crm.table.hoursAgo', { n: hour })
      const day = Math.floor(hour / 24)
      if (day < 30) return this.$t('crm.table.daysAgo', { n: day })
      const month = Math.floor(day / 30)
      if (month < 12) return this.$t('crm.table.monthsAgo', { n: month })
      const year = Math.floor(month / 12)
      return this.$t('crm.table.yearsAgo', { n: year })
    },
    getTimeClass(timeStr) {
      if (!timeStr) return ''
      const target = new Date(timeStr.replace(/-/g, '/')).getTime()
      if (isNaN(target)) return ''
      const diff = this.now - target
      const TWO_HOURS = 2 * 60 * 60 * 1000
      const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000
      if (diff >= SEVEN_DAYS) return 'crm-time-danger'
      if (diff >= TWO_HOURS) return 'crm-time-warning'
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.crm-container {
  padding: 20px;

  .crm-status-box {
    display: flex;
    align-items: center;
    gap: 10px;

    .crm-url-text {
      font-size: 13px;
      color: #606266;
    }

    .crm-url-link {
      color: #409eff;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .search-wrapper {
    margin-bottom: 20px;

    .el-row {
      margin-bottom: -20px;
    }

    .el-col {
      margin-bottom: 20px;
    }
  }

  .list-wrapper {
    .table-toolbar {
      margin-bottom: 15px;
    }

    .el-pagination {
      margin-top: 20px;
      text-align: right;
    }
  }

  .crm-disabled-tip {
    padding: 40px 0;
  }

  .crm-time-warning {
    color: #e6a23c;
    font-weight: 600;
  }

  .crm-time-danger {
    color: #f56c6c;
    font-weight: 600;
  }

  .crm-skip {
    color: #909399;
    font-size: 12px;
  }
}
</style>